import {
  type PdfPageBaseLayout,
  Button,
  FullScreenPage,
  Skeleton,
  Stack,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import {
  ESIGN_MARKUP_REFERRER_KEY,
  ESIGN_PAGE_WIDTH,
  isFillableFieldType,
} from "@/pages/esign/esign.config.ts";
import { EsignDocumentCard } from "@/pages/esign/components/EsignDocumentCard.tsx";
import { EsignFilePreview } from "@/pages/esign/components/EsignFilePreview.tsx";
import { EsignSuccessScreen } from "@/pages/esign/components/EsignSuccessScreen.tsx";
import {
  EsignFieldsLayer,
  type EsignPageFieldEntry,
} from "@/pages/esign/components/EsignFieldsLayer.tsx";
import { EsignSignerInput } from "@/pages/esign/widgets/EsignSignerModal.tsx";
import { useDocument, useOrganizationDocumentSave } from "@/queries/use-documents.query.ts";
import { useEsignMarkup, useEsignMarkupSave } from "@/queries/use-esign.query.ts";
import {
  ApiEsignMarkupField,
  ApiEsignMarkupRole,
  ApiEsignMarkupSave,
  EsignConditionType,
  EsignFieldFilledBy,
  EsignFieldType,
} from "@/types/esign.types.ts";
import { folderParamKeys } from "@/types/document.types.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const createEmployeeRole = (): ApiEsignMarkupRole => ({
  id: null,
  condition_type: EsignConditionType.affected_employee,
  condition_value: [],
  condition_entities: [],
  fields: [],
});

export default function EsignMarkupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { documentId } = useParams();

  const docId = documentId ? parseInt(documentId, 10) : undefined;

  const { data: document, isLoading: isDocumentLoading } = useDocument(docId ?? 0, !!docId);
  const { data: markup, isLoading: isMarkupLoading } = useEsignMarkup(docId ?? 0, !!docId);
  const markupSave = useEsignMarkupSave();
  const documentSave = useOrganizationDocumentSave();

  const isLoading = isDocumentLoading || isMarkupLoading;

  const [roles, setRoles] = useState<ApiEsignMarkupRole[]>([]);
  const [title, setTitle] = useState<string>(document?.title ?? "");
  const [isSaved, setIsSaved] = useState(false);
  const rolesInitedRef = useRef(false);

  useEffect(() => {
    if (rolesInitedRef.current || markup === undefined) return;
    const loaded = markup?.roles ?? [];
    const employee = loaded.find(
      (role) => role.condition_type === EsignConditionType.affected_employee
    );
    const rest = loaded.filter(
      (role) => role.condition_type !== EsignConditionType.affected_employee
    );
    setRoles([employee ?? createEmployeeRole(), ...rest]);
    rolesInitedRef.current = true;
  }, [markup]);

  const [syncedDocumentTitle, setSyncedDocumentTitle] = useState(document?.title);

  if (document?.title !== syncedDocumentTitle) {
    setSyncedDocumentTitle(document?.title);
    setTitle(document?.title ?? "");
  }

  useTitle(t("core:prepare_for_signing"), t("core:documents"));

  const fileName = document?.file?.name ?? "";
  const canEditTitle = !!(document?.folder && document?.file);

  const handleTitleCommit = () => {
    if (!canEditTitle || !document || title === document.title) return;
    void documentSave.mutateAsync({
      id: document.id,
      payload: {
        title,
        folder_id: document.folder.id,
        file_uuid: document.file!.uuid,
      },
    });
  };

  const handleAddSigner = (input: EsignSignerInput) => {
    setRoles((prev) => [
      ...prev,
      {
        id: null,
        condition_type: input.condition_type,
        condition_value: input.condition_value,
        condition_entities: input.condition_entities,
        fields: [],
      },
    ]);
  };

  const handleUpdateSigner = (index: number, input: EsignSignerInput) => {
    setRoles((prev) =>
      prev.map((role, i) =>
        i === index
          ? {
              ...role,
              condition_type: input.condition_type,
              condition_value: input.condition_value,
              condition_entities: input.condition_entities,
            }
          : role
      )
    );
  };

  const handleDeleteSigner = (index: number) => {
    if (index === 0) return;
    setRoles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddField = (roleIndex: number, field: Omit<ApiEsignMarkupField, "id">) => {
    setRoles((prev) =>
      prev.map((role, i) =>
        i === roleIndex ? { ...role, fields: [...role.fields, { id: null, ...field }] } : role
      )
    );
  };

  const handleUpdateField = (
    roleIndex: number,
    fieldIndex: number,
    patch: Partial<ApiEsignMarkupField>
  ) => {
    setRoles((prev) =>
      prev.map((role, i) =>
        i === roleIndex
          ? {
              ...role,
              fields: role.fields.map((f, j) => (j === fieldIndex ? { ...f, ...patch } : f)),
            }
          : role
      )
    );
  };

  const handleDeleteField = (roleIndex: number, fieldIndex: number) => {
    setRoles((prev) =>
      prev.map((role, i) =>
        i === roleIndex ? { ...role, fields: role.fields.filter((_, j) => j !== fieldIndex) } : role
      )
    );
  };

  const handleChangeFieldType = (
    roleIndex: number,
    fieldIndex: number,
    fieldType: EsignFieldType
  ) => {
    setRoles((prev) =>
      prev.map((role, i) =>
        i === roleIndex
          ? {
              ...role,
              fields: role.fields.map((field, j) => {
                if (j !== fieldIndex) return field;
                const fillable = isFillableFieldType(fieldType);
                return {
                  ...field,
                  field_type: fieldType,
                  filled_by: fillable
                    ? isFillableFieldType(field.field_type)
                      ? field.filled_by
                      : EsignFieldFilledBy.signer
                    : EsignFieldFilledBy.auto,
                  is_multiline: fieldType === EsignFieldType.text ? field.is_multiline : false,
                };
              }),
            }
          : role
      )
    );
  };

  const handleMoveFieldToRole = (
    roleIndex: number,
    fieldIndex: number,
    targetRoleIndex: number
  ) => {
    if (roleIndex === targetRoleIndex) return;
    setRoles((prev) => {
      const field = prev[roleIndex]?.fields[fieldIndex];
      if (!field) return prev;
      return prev.map((role, i) => {
        if (i === roleIndex) {
          return { ...role, fields: role.fields.filter((_, j) => j !== fieldIndex) };
        }
        if (i === targetRoleIndex) {
          return { ...role, fields: [...role.fields, field] };
        }
        return role;
      });
    });
  };

  const renderPageOverlay = (pageNumber: number, baseLayout: PdfPageBaseLayout) => {
    const entries: EsignPageFieldEntry[] = [];
    roles.forEach((role, roleIndex) => {
      role.fields.forEach((field, fieldIndex) => {
        if (field.page === pageNumber - 1) {
          entries.push({ roleIndex, fieldIndex, field });
        }
      });
    });

    return (
      <EsignFieldsLayer
        pageNumber={pageNumber}
        baseLayout={baseLayout}
        roles={roles}
        fields={entries}
        onAddField={handleAddField}
        onUpdateField={handleUpdateField}
        onDeleteField={handleDeleteField}
        onChangeFieldType={handleChangeFieldType}
        onMoveFieldToRole={handleMoveFieldToRole}
      />
    );
  };

  const handleSave = async () => {
    if (!docId) return;

    const payload: ApiEsignMarkupSave = {
      roles: roles.map((role) => ({
        id: role.id,
        condition_type: role.condition_type,
        condition_value: role.condition_value,
        fields: role.fields.map((field) => ({
          id: field.id,
          field_type: field.field_type,
          page: field.page,
          pos_x: field.pos_x,
          pos_y: field.pos_y,
          width: field.width,
          height: field.height,
          font_size: field.font_size,
          filled_by: field.filled_by,
          label: field.label,
          is_required: field.is_required,
          is_multiline: field.is_multiline,
        })),
      })),
    };
    try {
      await markupSave.mutateAsync({ documentId: docId, exists: !!markup, payload });
      analyticsService.trackEvent(analyticEvents.esign.markupSaved, {
        [analyticProperties.id]: docId,
        [analyticProperties.count]: roles.length,
      });
      setIsSaved(true);
    } catch {}
  };

  const folderPath = document?.folder
    ? `/docs?${folderParamKeys[document.folder.type]}=${document.folder.id}`
    : "/docs";

  const consumeReferrer = () => {
    const saved = sessionStorage.getItem(ESIGN_MARKUP_REFERRER_KEY);
    if (saved) sessionStorage.removeItem(ESIGN_MARKUP_REFERRER_KEY);
    return saved;
  };

  const navigateBack = () => {
    navigate(consumeReferrer() ?? folderPath);
  };

  const navigateToRequest = () => {
    const referrer = consumeReferrer();
    if (referrer && docId) {
      const url = new URL(referrer, window.location.origin);
      url.searchParams.set("esign_request", String(docId));
      navigate(url.pathname + url.search);
      return;
    }
    if (!document?.folder) return;
    const params = new URLSearchParams({
      [folderParamKeys[document.folder.type]]: String(document.folder.id),
      esign_request: String(document.id),
    });
    navigate(`/docs?${params.toString()}`);
  };

  if (isSaved) {
    return (
      <EsignSuccessScreen
        documentTitle={title}
        fileName={fileName}
        signerCount={roles.length}
        onDone={navigateBack}
        onRequest={navigateToRequest}
      />
    );
  }

  return (
    <FullScreenPage
      title={t("core:prepare_for_signing")}
      contentSize={"lg"}
      isCenterVertical={false}
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={navigateBack}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <Button
          variant={"primary"}
          size={"lg"}
          isLoading={markupSave.isPending}
          onClick={handleSave}
        >
          {t("common:save")}
        </Button>
      }
    >
      {isLoading ? (
        <div className={"mx-auto w-full"} style={{ maxWidth: ESIGN_PAGE_WIDTH }}>
          <Skeleton className={"h-64 w-full"} />
        </div>
      ) : (
        <Stack direction={"vertical"} items={"center"} className={"gap-8 pb-16"}>
          <EsignDocumentCard
            fileName={fileName}
            title={title}
            onTitleChange={setTitle}
            onTitleCommit={handleTitleCommit}
            roles={roles}
            onAddSigner={handleAddSigner}
            onUpdateSigner={handleUpdateSigner}
            onDeleteSigner={handleDeleteSigner}
          />
          {document?.file && (
            <EsignFilePreview
              file={document.file}
              preview={document.preview}
              documentId={document.id}
              maxRenderWidth={ESIGN_PAGE_WIDTH}
              renderPageOverlay={renderPageOverlay}
            />
          )}
        </Stack>
      )}
    </FullScreenPage>
  );
}
